import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Container, Typography, Card, CardContent,
  Button, Chip, Stack, Divider, CircularProgress, Alert,
  IconButton, useMediaQuery, useTheme,
} from '@mui/material';
import {
  CheckCircle, Star, ChevronLeft, ChevronRight,
} from '@mui/icons-material';
import { CONTACT_EMAIL } from '../constants/contact';

const COURIER_URL = process.env.REACT_APP_COURIER_URL || 'http://localhost:3000';
const DRIVER_URL = process.env.REACT_APP_DRIVER_URL || 'http://localhost:3001';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Plan {
  name: string;
  price: string;
  limit: string;
  description: string;
  features: string[];
  popular?: boolean;
  cta: string;
  href: string;
}

interface ApiPlan {
  _id: string;
  name: string;
  entity_limit: number;
  price: number;
  features: string[];
  display_order: number;
  panel_type: 'courier' | 'driver';
}

const fallbackCourier: Plan[] = [
  {
    name: 'Essential',
    price: '€19.90',
    limit: 'Até 50 estafetas / semana',
    description: 'Ideal para pequenas operações de estafetas a arrancar.',
    features: [
      'Até 50 estafetas por semana',
      'Relatórios semanais ilimitados',
      'Geração de ficheiros SEPA',
      'Exportação de faturas em PDF',
      'Apoio por correio eletrónico',
    ],
    cta: 'Começar com Essential',
    href: `${COURIER_URL}/register`,
  },
  {
    name: 'Pro',
    price: '€39.90',
    limit: 'Até 150 estafetas / semana',
    description: 'Para equipas em crescimento com mais capacidade e funcionalidades.',
    features: [
      'Até 150 estafetas por semana',
      'Relatórios semanais ilimitados',
      'Geração de ficheiros SEPA',
      'Faturas em PDF + correio + WhatsApp',
      'Gestão de parceiros',
      'Apoio prioritário',
    ],
    popular: true,
    cta: 'Começar com Pro',
    href: `${COURIER_URL}/register`,
  },
  {
    name: 'Enterprise',
    price: '€99.90',
    limit: 'Até 1 000 estafetas / semana',
    description: 'Para operações de estafetas em grande escala.',
    features: [
      'Até 1 000 estafetas por semana',
      'Relatórios semanais ilimitados',
      'Geração de ficheiros SEPA',
      'Suite completa de faturação',
      'Análises avançadas',
      'Apoio dedicado por conta',
    ],
    cta: 'Começar com Enterprise',
    href: `${COURIER_URL}/register`,
  },
];

const fallbackDriver: Plan[] = [
  {
    name: 'Essential',
    price: '€19.90',
    limit: 'Até 50 motoristas / semana',
    description: 'Excelente para pequenas frotas a começar.',
    features: [
      'Até 50 motoristas por semana',
      'Gestão de veículos',
      'Relatórios semanais de motoristas',
      'Geração de ficheiros SEPA',
      'Apoio por correio eletrónico',
    ],
    cta: 'Começar com Essential',
    href: `${DRIVER_URL}/register`,
  },
  {
    name: 'Pro',
    price: '€39.90',
    limit: 'Até 150 motoristas / semana',
    description: 'Para operadores de frota com vários veículos e parceiros.',
    features: [
      'Até 150 motoristas por semana',
      'Gestão da frota de veículos',
      'Relatórios de comissões de parceiros',
      'Acompanhamento de rendas a proprietários',
      'Faturas em PDF + correio + WhatsApp',
      'Apoio prioritário',
    ],
    popular: true,
    cta: 'Começar com Pro',
    href: `${DRIVER_URL}/register`,
  },
  {
    name: 'Enterprise',
    price: '€99.90',
    limit: 'Até 1 000 motoristas / semana',
    description: 'Para grandes frotas com operações complexas.',
    features: [
      'Até 1 000 motoristas por semana',
      'Gestão completa da frota',
      'Todos os tipos de relatório',
      'Suite completa de faturação',
      'Análises avançadas',
      'Apoio dedicado',
    ],
    cta: 'Começar com Enterprise',
    href: `${DRIVER_URL}/register`,
  },
];

function isPopularPlan(plan: ApiPlan, index: number, total: number): boolean {
  if (/pro/i.test(plan.name)) return true;
  if (total >= 3 && index === Math.floor(total / 2)) return true;
  return false;
}

function mapApiPlans(
  rows: ApiPlan[],
  registerBase: string,
  entityLabel: 'estafetas' | 'motoristas'
): Plan[] {
  const sorted = [...rows].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.price - b.price
  );
  return sorted.map((p, index) => ({
    name: p.name,
    price: `€${Number(p.price).toFixed(2)}`,
    limit: `Até ${p.entity_limit} ${entityLabel} / semana`,
    description:
      p.features?.[0] ||
      `NeoDrive ${p.name} — até ${p.entity_limit} ${entityLabel} por semana.`,
    features:
      p.features?.length > 0
        ? p.features
        : [
            `Até ${p.entity_limit} ${entityLabel} por semana`,
            'Relatórios semanais e ferramentas de faturação',
            'Fluxos preparados para SEPA',
          ],
    popular: isPopularPlan(p, index, sorted.length),
    cta: `Começar com ${p.name}`,
    href: registerBase,
  }));
}

function PlanCard({
  plan,
  accentColor,
}: {
  plan: Plan;
  accentColor: string;
}) {
  return (
    <Card
      sx={{
        height: '100%',
        position: 'relative',
        border: plan.popular ? `2px solid ${accentColor}` : '1px solid #e2e8f0',
        boxShadow: plan.popular
          ? `0 20px 60px ${accentColor}20`
          : '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.3s ease, transform 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
        mx: { xs: 0.5, sm: 1 },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: plan.popular
            ? `0 28px 70px ${accentColor}30`
            : '0 12px 30px rgba(0,0,0,0.08)',
        },
      }}
    >
      {plan.popular && (
        <Box
          sx={{
            position: 'absolute',
            top: -14,
            left: '50%',
            transform: 'translateX(-50%)',
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
            color: '#fff',
            px: 2.5,
            py: 0.6,
            borderRadius: 10,
            fontSize: '0.72rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            whiteSpace: 'nowrap',
            boxShadow: `0 4px 12px ${accentColor}40`,
            zIndex: 1,
          }}
        >
          <Star sx={{ fontSize: 12 }} />
          MAIS POPULAR
        </Box>
      )}
      <CardContent sx={{ p: 3.5, display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {plan.name}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.85rem', mb: 2 }}>
            {plan.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              sx={{
                fontSize: '2.5rem',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: plan.popular ? accentColor : '#0f172a',
              }}
            >
              {plan.price}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>/mês</Typography>
          </Box>
          <Chip
            label={plan.limit}
            size="small"
            sx={{
              mt: 1,
              background: plan.popular ? `${accentColor}15` : '#f1f5f9',
              color: plan.popular ? accentColor : '#475569',
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 24,
            }}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={1.2} sx={{ flex: 1, mb: 3 }}>
          {plan.features.map((feat) => (
            <Box key={feat} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <CheckCircle sx={{ color: accentColor, fontSize: 17, mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{feat}</Typography>
            </Box>
          ))}
        </Stack>

        <Button
          variant={plan.popular ? 'contained' : 'outlined'}
          fullWidth
          href={plan.href}
          sx={
            plan.popular
              ? {
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                  py: 1.4,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${accentColor}cc, ${accentColor}aa)`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 20px ${accentColor}40`,
                  },
                }
              : {
                  borderColor: '#d1d5db',
                  color: '#374151',
                  py: 1.4,
                  '&:hover': {
                    borderColor: accentColor,
                    color: accentColor,
                    background: `${accentColor}08`,
                  },
                }
          }
        >
          {plan.cta}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Pricing() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [courierPlans, setCourierPlans] = useState<Plan[]>(fallbackCourier);
  const [driverPlans, setDriverPlans] = useState<Plan[]>(fallbackDriver);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);

  const [touchStart, setTouchStart] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setFetchError(null);
      try {
        const [cRes, dRes] = await Promise.all([
          fetch(`${API_URL}/api/courier/subscriptions/plans`),
          fetch(`${API_URL}/api/driver/subscriptions/plans`),
        ]);
        if (!cRes.ok || !dRes.ok) {
          throw new Error('Plans API returned an error');
        }
        const courierRaw: ApiPlan[] = await cRes.json();
        const driverRaw: ApiPlan[] = await dRes.json();
        if (cancelled) return;
        if (courierRaw.length > 0) {
          setCourierPlans(mapApiPlans(courierRaw, `${COURIER_URL}/register`, 'estafetas'));
        }
        if (driverRaw.length > 0) {
          setDriverPlans(mapApiPlans(driverRaw, `${DRIVER_URL}/register`, 'motoristas'));
        }
      } catch {
        if (!cancelled) {
          setFetchError('Não foi possível carregar os planos em tempo real — a mostrar preços predefinidos. Verifique o URL da API e o CORS.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const plans = activeTab === 0 ? courierPlans : driverPlans;
  const accentColor = activeTab === 0 ? '#16a34a' : '#ea580c';

  const slidesVisible = useMemo(() => {
    const n = plans.length;
    if (n <= 1) return 1;
    if (isMdUp) return Math.min(3, n);
    if (isSmUp) return Math.min(2, n);
    return 1;
  }, [plans.length, isMdUp, isSmUp]);

  const maxSlide = Math.max(0, plans.length - slidesVisible);

  useEffect(() => {
    setSlideIndex(0);
  }, [activeTab]);

  useEffect(() => {
    setSlideIndex((i) => Math.min(i, maxSlide));
  }, [maxSlide]);

  const goPrev = useCallback(() => {
    setSlideIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setSlideIndex((i) => Math.min(maxSlide, i + 1));
  }, [maxSlide]);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStart === null) return;
      const end = e.changedTouches[0].clientX;
      const diff = touchStart - end;
      if (Math.abs(diff) > 50) {
        if (diff > 0) goNext();
        else goPrev();
      }
      setTouchStart(null);
    },
    [touchStart, goNext, goPrev]
  );

  return (
    <Box
      id="pricing"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip
            label="PREÇOS"
            sx={{
              mb: 2,
              background: 'rgba(234,88,12,0.08)',
              color: '#ea580c',
              fontWeight: 700,
              fontSize: '0.7rem',
              letterSpacing: '0.1em',
              height: 28,
            }}
          />
          <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, mb: 2 }}>
            Preços simples e transparentes
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '1.05rem', maxWidth: 500, mx: 'auto', mb: 1 }}>
            Comece grátis. Faça upgrade à medida que cresce. Sem taxas ocultas, sem surpresas.
          </Typography>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Todos os planos incluem período experimental gratuito · Cancele quando quiser
          </Typography>
        </Box>

        {fetchError && (
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 720, mx: 'auto' }}>
            {fetchError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
          <Box sx={{ background: '#f1f5f9', borderRadius: 3, p: 0.6, display: 'inline-flex' }}>
            {['📦  Planos estafetas', '🚗  Planos motoristas'].map((label, i) => (
              <Box
                key={label}
                onClick={() => setActiveTab(i)}
                sx={{
                  px: 3,
                  py: 1.2,
                  borderRadius: 2.2,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  transition: 'all 0.2s ease',
                  color: activeTab === i ? '#fff' : '#64748b',
                  background: activeTab === i
                    ? (i === 0 ? '#16a34a' : '#ea580c')
                    : 'transparent',
                  boxShadow: activeTab === i
                    ? `0 4px 12px ${i === 0 ? '#16a34a' : '#ea580c'}40`
                    : 'none',
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: accentColor }} />
          </Box>
        ) : (
          <Box sx={{ position: 'relative', px: { xs: 0, sm: 5, md: 7 } }}>
            {maxSlide > 0 && (
              <>
                <IconButton
                  onClick={goPrev}
                  disabled={slideIndex <= 0}
                  aria-label="Planos anteriores"
                  sx={{
                    position: 'absolute',
                    left: { xs: -8, sm: 0, md: -8 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'grey.50' },
                    '&.Mui-disabled': { opacity: 0.35 },
                  }}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={goNext}
                  disabled={slideIndex >= maxSlide}
                  aria-label="Planos seguintes"
                  sx={{
                    position: 'absolute',
                    right: { xs: -8, sm: 0, md: -8 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'grey.50' },
                    '&.Mui-disabled': { opacity: 0.35 },
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}

            <Box
              sx={{
                overflow: 'hidden',
                borderRadius: 2,
                /* Espaço para o selo "MAIS POPULAR" (acima do cartão com top negativo) */
                pt: { xs: 3.5, sm: 4 },
                pb: 2,
              }}
              onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
              onTouchEnd={onTouchEnd}
            >
              <Box
                sx={{
                  display: 'flex',
                  width: `${(plans.length / slidesVisible) * 100}%`,
                  transform: `translateX(-${(slideIndex * 100) / plans.length}%)`,
                  transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {plans.map((plan) => (
                  <Box
                    key={`${activeTab}-${plan.name}`}
                    sx={{
                      width: `${100 / plans.length}%`,
                      flexShrink: 0,
                      boxSizing: 'border-box',
                      px: { xs: 0.5, sm: 1 },
                    }}
                  >
                    <PlanCard plan={plan} accentColor={accentColor} />
                  </Box>
                ))}
              </Box>
            </Box>

            {plans.length > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1,
                  mt: 3,
                  flexWrap: 'wrap',
                }}
              >
                {Array.from({ length: maxSlide + 1 }, (_, i) => (
                  <Box
                    key={i}
                    component="button"
                    type="button"
                    onClick={() => setSlideIndex(i)}
                    sx={{
                      width: slideIndex === i ? 28 : 8,
                      height: 8,
                      borderRadius: 4,
                      border: 'none',
                      p: 0,
                      cursor: 'pointer',
                      bgcolor: slideIndex === i ? accentColor : 'rgba(0,0,0,0.12)',
                      transition: 'all 0.3s ease',
                      '&:hover': { bgcolor: slideIndex === i ? accentColor : 'rgba(0,0,0,0.22)' },
                    }}
                    aria-label={`Ir para o slide ${i + 1}`}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Precisa de mais de 1 000 membros por semana?{' '}
            <Box
              component="a"
              href={`mailto:${CONTACT_EMAIL}`}
              sx={{
                color: accentColor,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Contacte-nos
            </Box>{' '}
            para um plano à medida.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
